// Shared magical-flame palette: warm golds through magenta/violet into electric blue.
export const EMBER_COLORS = [
  { r: 255, g: 170, b: 90 },
  { r: 255, g: 212, b: 130 },
  { r: 255, g: 100, b: 200 },
  { r: 186, g: 110, b: 255 },
  { r: 105, g: 180, b: 255 },
  { r: 80, g: 130, b: 255 },
];

export const pickEmberColor = () => {
  const roll = Math.random();
  if (roll < 0.42) return EMBER_COLORS[Math.random() < 0.5 ? 0 : 1];
  if (roll < 0.75) return EMBER_COLORS[Math.random() < 0.5 ? 2 : 3];
  return EMBER_COLORS[Math.random() < 0.5 ? 4 : 5];
};

export const rgba = (c, a) => `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`;

// Soft radial glow sprite for bokeh-style particles (drawn via drawImage for speed).
const spriteCache = new Map();
export const glowSprite = (c, size = 48) => {
  if (spriteCache.has(c)) return spriteCache.get(c);
  const cv = document.createElement('canvas');
  cv.width = size;
  cv.height = size;
  const g = cv.getContext('2d');
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, rgba(c, 0.95));
  grad.addColorStop(0.35, rgba(c, 0.4));
  grad.addColorStop(1, rgba(c, 0));
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  spriteCache.set(c, cv);
  return cv;
};

// Two-pass wisp stroke: wide soft halo + bright core, for flame-ribbon trails.
export const drawWisp = (ctx, trail, c, alpha, width) => {
  if (trail.length < 2) return;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(trail[0].x, trail[0].y);
  for (let t = 1; t < trail.length; t++) ctx.lineTo(trail[t].x, trail[t].y);
  ctx.strokeStyle = rgba(c, alpha * 0.3);
  ctx.lineWidth = width * 3.4;
  ctx.stroke();
  ctx.strokeStyle = rgba({ r: Math.min(255, c.r + 70), g: Math.min(255, c.g + 70), b: Math.min(255, c.b + 70) }, alpha);
  ctx.lineWidth = Math.max(width, 0.4);
  ctx.stroke();
};

// Flame journey gradient: electric blue -> violet -> magenta -> orange -> gold.
const FLAME_STOPS = [
  { r: 80, g: 140, b: 255 },
  { r: 150, g: 110, b: 255 },
  { r: 235, g: 100, b: 220 },
  { r: 255, g: 140, b: 100 },
  { r: 255, g: 212, b: 130 },
];

export const flameColor = (t) => {
  const x = Math.min(Math.max(t, 0), 1) * (FLAME_STOPS.length - 1);
  const i = Math.min(Math.floor(x), FLAME_STOPS.length - 2);
  const f = x - i;
  const a = FLAME_STOPS[i];
  const b = FLAME_STOPS[i + 1];
  return {
    r: (a.r + (b.r - a.r) * f) | 0,
    g: (a.g + (b.g - a.g) * f) | 0,
    b: (a.b + (b.b - a.b) * f) | 0,
  };
};

const flameSpriteCache = [];
export const flameSprite = (t) => {
  const idx = Math.round(Math.min(Math.max(t, 0), 1) * 10);
  if (!flameSpriteCache[idx]) flameSpriteCache[idx] = glowSprite(flameColor(idx / 10));
  return flameSpriteCache[idx];
};

// Extra-diffuse sprite for nebula gas clouds (long soft falloff).
const softCache = new Map();
export const softSprite = (c, size = 64) => {
  if (softCache.has(c)) return softCache.get(c);
  const cv = document.createElement('canvas');
  cv.width = size;
  cv.height = size;
  const g = cv.getContext('2d');
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, rgba(c, 0.5));
  grad.addColorStop(0.45, rgba(c, 0.18));
  grad.addColorStop(1, rgba(c, 0));
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  softCache.set(c, cv);
  return cv;
};

const flameSoftCache = [];
export const flameSoftSprite = (t) => {
  const idx = Math.round(Math.min(Math.max(t, 0), 1) * 10);
  if (!flameSoftCache[idx]) flameSoftCache[idx] = softSprite(flameColor(idx / 10));
  return flameSoftCache[idx];
};

// Soft gas/plasma blob: glow sprite stretched along its motion direction.
export const drawGasBlob = (ctx, sprite, x, y, size, angle, stretch, alpha) => {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.scale(stretch, 1);
  ctx.drawImage(sprite, -size / 2, -size / 2, size, size);
  ctx.restore();
};

// Sinuous tapered flame ribbon: soft halo polyline + per-segment bright core
// that tapers in width/alpha and carries each point's own journey color.
export const drawFlameWisp = (ctx, trail, alpha, width) => {
  if (trail.length < 2) return;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const mid = trail[Math.floor(trail.length * 0.6)].c;
  ctx.strokeStyle = rgba(mid, alpha * 0.26);
  ctx.lineWidth = width * 3.2;
  ctx.beginPath();
  ctx.moveTo(trail[0].x, trail[0].y);
  for (let i = 1; i < trail.length; i++) ctx.lineTo(trail[i].x, trail[i].y);
  ctx.stroke();
  for (let i = 1; i < trail.length; i++) {
    const t = i / (trail.length - 1);
    const c = trail[i].c;
    ctx.strokeStyle = rgba(
      { r: Math.min(255, c.r + 60), g: Math.min(255, c.g + 60), b: Math.min(255, c.b + 60) },
      alpha * (0.3 + 0.7 * t)
    );
    ctx.lineWidth = Math.max(width * (0.3 + 0.7 * t), 0.4);
    ctx.beginPath();
    ctx.moveTo(trail[i - 1].x, trail[i - 1].y);
    ctx.lineTo(trail[i].x, trail[i].y);
    ctx.stroke();
  }
};
